import {
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowUpRight,
  CreditCard,
  HandCoins,
  Plus,
  ShoppingCart,
  Wallet,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { confirmModal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { useAccounts, useAccountSummary, useDeleteAccount } from './api'
import { AccountCard } from './components/AccountCard'
import { AccountForm } from './components/AccountForms'
import { CashEntryForm } from './components/CashEntryForm'
import { Money, PeriodSelect } from './components/FinanceBits'
import { MovementsSection, type MovementFilter } from './components/MovementsSection'
import { TransferForm } from './components/TransferForm'
import { CreditCardsSection } from './components/credit/CreditCardsSection'
import { cents, sumMoney, toMoney } from './money'
import { FINANCE_ACCOUNT_WRITE } from './permissions'
import { SCOPE_ACCOUNT_TYPES, type AccountScope, type FinAccount } from './types'
import { monthLabel, toastError, usePeriod } from './ui'

type Transfer = { fromId?: string; toId?: string }

function Totals({ items }: { items: { label: string; value: string | undefined; tone?: string }[] }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      {items.map((x) => (
        <div key={x.label} className="min-w-0">
          <p className="text-muted-foreground text-sm">{x.label}</p>
          <div className={cn('text-xl font-semibold tracking-tight sm:text-2xl', x.tone)}>
            {x.value !== undefined ? <Money value={x.value} /> : <Skeleton className="h-8 w-40" />}
          </div>
        </div>
      ))}
    </div>
  )
}

/** `money`: kasa, banka ve POS hesapları; `cards`: kredi kartları. Aynı ekran iki ayrı menüde, ayrı kapsamla çalışır. */
export function FinanceAccountsPage({ scope }: { scope: AccountScope }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { month, setMonth } = usePeriod()
  const accounts = useAccounts()
  const summaries = useAccountSummary(month)
  const removeAccount = useDeleteAccount()
  const [filter, setFilter] = useState<MovementFilter>({ accountId: 'all', direction: 'all', scope: 'period' })
  const [accountForm, setAccountForm] = useState<{ open: boolean; account: FinAccount | null }>({
    open: false,
    account: null,
  })
  const [transfer, setTransfer] = useState<Transfer | null>(null)
  const [entry, setEntry] = useState<{ direction: 'in' | 'out'; accountId?: string } | null>(null)
  const canWrite = can(FINANCE_ACCOUNT_WRITE)
  const isCards = scope === 'cards'
  const types = SCOPE_ACCOUNT_TYPES[scope]
  const list = (accounts.data ?? []).filter((a) => types.includes(a.type))
  const loaded = !accounts.isLoading
  const summaryOf = new Map((summaries.data ?? []).map((s) => [s.accountId, s]))
  const periodLabel = monthLabel(month)

  const onDeleteAccount = async (a: FinAccount) => {
    if (
      !(await confirmModal({
        title: t('finance.accounts.deleteTitle'),
        description: t('finance.accounts.deleteBody', { name: a.name }),
        confirmLabel: t('common.delete'),
        destructive: true,
      }))
    )
      return
    removeAccount.mutate(a.id, { onSuccess: () => toast.success(t('finance.accounts.deleted')), onError: toastError })
  }

  const creditCards = list.filter((a) => a.type === 'credit_card')
  const firstActiveCard = creditCards.find((a) => a.active)
  const debt = toMoney(-creditCards.reduce((acc, a) => (cents(a.balance) < 0n ? acc + cents(a.balance) : acc), 0n))
  const totals = isCards
    ? [
        {
          label: t('finance.cards.bankTotal'),
          value: loaded ? sumMoney(list.filter((a) => a.type === 'bank').map((a) => a.balance)) : undefined,
        },
        { label: t('finance.cards.totalDebt'), value: loaded ? debt : undefined, tone: 'text-destructive' },
        {
          label: t('finance.cards.available'),
          value: loaded ? sumMoney(creditCards.map((a) => a.availableLimit ?? '0')) : undefined,
          tone: 'text-success',
        },
      ]
    : [{ label: t('finance.accounts.total'), value: loaded ? sumMoney(list.map((a) => a.balance)) : undefined }]

  const addLabel = t(isCards ? 'finance.cards.add' : 'finance.accounts.add')
  const openAdd = () => setAccountForm({ open: true, account: null })

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.finance')}
        title={t(isCards ? 'finance.cards.title' : 'finance.accounts.title')}
        description={t(isCards ? 'finance.cards.subtitle' : 'finance.accounts.subtitle')}
        actions={
          <>
            <PeriodSelect value={month} onChange={setMonth} />
            {canWrite && isCards ? (
              <>
                <Button
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setEntry({ direction: 'out', accountId: firstActiveCard?.id })}
                  disabled={!firstActiveCard}
                >
                  <ShoppingCart />
                  {t('finance.cards.spend')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setTransfer({ toId: firstActiveCard?.id })}
                  disabled={!firstActiveCard}
                >
                  <HandCoins />
                  {t('finance.cards.payDebt')}
                </Button>
              </>
            ) : canWrite ? (
              <>
                <Button
                  variant="outline"
                  className="text-success hover:text-success"
                  onClick={() => setEntry({ direction: 'in' })}
                >
                  <ArrowDownLeft />
                  {t('finance.entry.addIn')}
                </Button>
                <Button
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setEntry({ direction: 'out' })}
                >
                  <ArrowUpRight />
                  {t('finance.entry.addOut')}
                </Button>
                <Button variant="outline" onClick={() => setTransfer({})}>
                  <ArrowLeftRight />
                  {t('finance.accounts.transfer')}
                </Button>
              </>
            ) : null}
            {canWrite ? (
              <Button onClick={openAdd}>
                <Plus />
                {addLabel}
              </Button>
            ) : null}
          </>
        }
      />

      {accounts.isError ? (
        <ErrorState error={accounts.error} onRetry={() => void accounts.refetch()} />
      ) : (
        <div className="space-y-4">
          <div className="border-primary/20 bg-primary/[0.05] shadow-card space-y-3 rounded-xl border p-4">
            <Totals items={totals} />
            <p className="text-muted-foreground text-xs">
              {t(isCards ? 'finance.cards.totalHint' : 'finance.accounts.totalHint')}
            </p>
          </div>

          {loaded && list.length === 0 ? (
            <div className="bg-card shadow-card rounded-xl border">
              <EmptyState
                icon={isCards ? CreditCard : Wallet}
                title={t(isCards ? 'finance.cards.empty' : 'finance.accounts.empty')}
                description={t(isCards ? 'finance.cards.emptyHint' : 'finance.accounts.emptyHint')}
                action={
                  canWrite ? (
                    <Button size="sm" className="mt-2" onClick={openAdd}>
                      <Plus />
                      {addLabel}
                    </Button>
                  ) : null
                }
              />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {accounts.isLoading
                  ? Array.from({ length: 2 }, (_, i) => <Skeleton key={i} className="h-80 rounded-xl" />)
                  : list.map((a) => (
                      <AccountCard
                        key={a.id}
                        account={a}
                        summary={summaryOf.get(a.id)}
                        periodLabel={periodLabel}
                        canWrite={canWrite}
                        selected={filter.accountId === a.id}
                        onSelect={() => setFilter({ accountId: a.id, direction: 'all', scope: 'period' })}
                        onEntry={(direction) => setEntry({ direction, accountId: a.id })}
                        onTransfer={() => setTransfer(a.type === 'credit_card' ? { toId: a.id } : { fromId: a.id })}
                        onEdit={() => setAccountForm({ open: true, account: a })}
                        onDelete={() => void onDeleteAccount(a)}
                      />
                    ))}
              </div>

              <MovementsSection
                title={t(isCards ? 'finance.cards.movements' : 'finance.accounts.movements')}
                allLabel={t(isCards ? 'finance.cards.allCards' : 'finance.accounts.allAccounts')}
                accounts={list}
                filter={filter}
                onFilter={setFilter}
                month={month}
                periodLabel={periodLabel}
                summary={summaryOf.get(filter.accountId)}
                canWrite={canWrite}
              />
              {isCards ? <CreditCardsSection scope="company" canWrite={canWrite} /> : null}
            </>
          )}
        </div>
      )}

      {accountForm.open ? (
        <AccountForm
          key={accountForm.account?.id ?? 'new'}
          scope={scope}
          account={accountForm.account}
          onDone={() => setAccountForm({ open: false, account: null })}
        />
      ) : null}
      {transfer ? (
        <TransferForm fromId={transfer.fromId} toId={transfer.toId} onDone={() => setTransfer(null)} />
      ) : null}
      {entry ? (
        <CashEntryForm
          key={`${entry.direction}-${entry.accountId ?? ''}`}
          direction={entry.direction}
          accountId={entry.accountId}
          accountTypes={types}
          title={creditCards.some((c) => c.id === entry.accountId) ? t('finance.cards.spendTitle') : undefined}
          onDone={() => setEntry(null)}
        />
      ) : null}
    </>
  )
}
